'use memo';

import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { Badge } from '@/components/Badge';
import IconButton from '@/components/Button/IconButton';
import { Card } from '@/components/Card/card';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { accessRulesKeyPrefix, useAccessRuleList, useAuthenticationPolicyLabels, useDeleteAccessRule } from '@/features/authentication-policy';
import { PolicySectionHeader } from '@/features/identity-policy';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { handleApiError } from '@/lib/api/error';
import type { AuthenticationPolicyAccessRuleView } from '@/lib/api/orpc/contracts/authentication-policy-access-rule';
import { defaultNS, useTranslation } from '@/lib/i18n';
export const Route = createFileRoute('/_staff/settings/authentication/_layout/access-rules/')({
  component: AccessRulesPage
});
function AccessRulesPage() {
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: accessRulesKeyPrefix
  });
  const tc = useCommonTranslation();
  const {
    membershipTypeLabel
  } = useAuthenticationPolicyLabels();
  const {
    data,
    isPending,
    isError
  } = useAccessRuleList();
  const deleteMutation = useDeleteAccessRule();
  const [deleteTarget, setDeleteTarget] = useState<AuthenticationPolicyAccessRuleView | null>(null);
  if (isPending) return <ViewportLoader />;
  if (isError || data == null) {
    return <VStack className="w-full gap-4 px-12 py-6">
        <PolicySectionHeader iconName="ShieldIcon" title={t($ => $.title)} description={t($ => $.description)} />
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </VStack>;
  }
  const handleDelete = async () => {
    if (deleteTarget == null) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id
      });
      setDeleteTarget(null);
      toast({
        description: t($ => $.toast.deleted)
      });
    } catch (error) {
      toast({
        description: handleApiError(error, {
          language: i18n.language
        }).message,
        variant: 'destructive'
      });
    }
  };
  return <VStack className="w-full gap-4 px-12 py-6">
      <PolicySectionHeader iconName="ShieldIcon" title={t($ => $.title)} description={t($ => $.description)} />

      <HStack className="justify-end">
        <Button asChild>
          <Link to="/settings/authentication/access-rules/new">
            {t($ => $.list.create)}
          </Link>
        </Button>
      </HStack>

      {data.items.length === 0 ? <Card className="p-6">
          <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
            {t($ => $.list.empty)}
          </Text>
        </Card> : <VStack className="gap-2">
          {data.items.map(accessRule => <Card key={accessRule.id} className="p-4">
              <HStack className="items-start gap-3">
                <VStack className="min-w-0 flex-1 gap-2">
                  <Link to="/settings/authentication/access-rules/$id" params={{
              id: accessRule.id
            }} className="min-w-0">
                    <Text variant="labelSemiLarge" truncate className="text-primary-on-neutral-fill">
                      {accessRule.name}
                    </Text>
                  </Link>

                  <HStack className="items-center gap-2">
                    <Text variant="labelSmallReg" className="shrink-0 text-neutral-muted-on-fill">
                      {t($ => $.list.columns.policy)}
                    </Text>
                    <Text variant="labelSmall" truncate>
                      {accessRule.policy.name}
                    </Text>
                  </HStack>

                  <HStack className="flex-wrap items-center gap-1">
                    <Text variant="labelSmallReg" className="shrink-0 text-neutral-muted-on-fill">
                      {t($ => $.list.columns.targets)}
                    </Text>
                    {accessRule.assignments.map(assignment => <Badge key={assignment.targetKind === 'PEOPLE_GROUP' ? `group-${assignment.peopleGroupId}` : `membership-${assignment.membershipType}`} variant="neutralMuted">
                        {assignment.targetKind === 'PEOPLE_GROUP' ?
                // 解決できないグループ（削除済み等）は ID を出して気づけるようにする
                assignment.peopleGroup?.name ?? assignment.peopleGroupId : membershipTypeLabel(assignment.membershipType)}
                      </Badge>)}
                  </HStack>

                  <HStack className="items-center gap-2">
                    <Text variant="labelSmallReg" className="shrink-0 text-neutral-muted-on-fill">
                      {t($ => $.list.columns.applicationScope)}
                    </Text>
                    <Text variant="labelSmallReg">
                      {accessRule.applicationScope == null ? t($ => $.list.applicationScopeAll) : t($ => $.list.applicationScopeCount, {
                  count: accessRule.applicationScope.length
                })}
                    </Text>
                  </HStack>
                </VStack>

                <IconButton icon="Trash2Icon" size="Small" variant="ghost" aria-label={tc('action.delete')} onClick={() => setDeleteTarget(accessRule)} />
              </HStack>
            </Card>)}
        </VStack>}

      <ConfirmActionDialog isOpen={deleteTarget != null} isLoading={deleteMutation.isPending} title={t($ => $.delete.title)} description={t($ => $.delete.description)} okLabel={t($ => $.delete.confirm)} okVariant="danger" onClose={() => setDeleteTarget(null)} onConfirm={() => {
      void handleDelete();
    }} />
    </VStack>;
}